import React from "react";
import { isBoolean, map, sum } from "lodash";
import { createMemo } from "react-use";
import MetricTrend from "@charty/metric-trend";
import { Color, isColor } from "@glossy";
import { AbstractDate, Interval, Range } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { diff } from "@tootools/date";
import {
  division,
  evolution,
  evolutionPercentagePoint,
} from "@tootools/number";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { DatavizKpi } from "@radarly/models/dataviz";
import {
  getComparaisonPeriodFromFragments,
  hasComparaisonFragment,
} from "@radarly/models/fragment";
import { SearchFragmentDateMetrics } from "@radarly/models/search";
import { useRangeFromFragment } from "../../data";

export interface SharedWidgetsKpiInnerProps {
  fragment: SearchFragmentDateMetrics;
  dataviz: DatavizKpi;
  comparaisonData: any;
  data: any;
  loading: boolean;
  evolutionFragment?: SearchFragmentDateMetrics;
  onClick?: () => void;
  disableAnimation?: boolean;
  className?: string;
  nowDate?: AbstractDate;
  isFullscreen?: boolean;
}

function getDailyMean(total: number, range: Range) {
  if (range.interval === Interval.Hour) {
    return division(
      total,
      24 / diff(range.from, range.to, Interval.Hour)
    ) as number;
  } else {
    return division(total, diff(range.from, range.to, Interval.Day)) as number;
  }
}

function getTrends(
  counts: SearchFragmentDateMetrics["data"]["aggs"]["date"]["values"],
  metric: Metric
) {
  return map(map(counts, "counts"), metric);
}

function getKpiCount(
  useDailyMetrics: DatavizKpi["dailyMetrics"],
  counts: SearchFragmentDateMetrics["data"]["aggs"]["date"]["values"],
  range: Range,
  metric: Metric
) {
  const total = sum(getTrends(counts, metric));
  return useDailyMetrics ? getDailyMean(total, range) : total;
}

function getKpiCountFromData(
  useDailyMetrics: DatavizKpi["dailyMetrics"],
  data: SearchFragmentDateMetrics["data"],
  range: Range,
  metric: Metric
) {
  if (!data) {
    return null;
  }
  if (data.aggs.metrics) {
    const total = data.aggs.metrics.metrics[metric];
    return useDailyMetrics && metric !== Metric.NetSentiment
      ? getDailyMean(total, range)
      : total;
  } else {
    return getKpiCount(useDailyMetrics, data.aggs.date.values, range, metric);
  }
}

function getEvolution(metric: Metric, count: number, beforeCount: number) {
  if (metric === Metric.NetSentiment) {
    return evolutionPercentagePoint(beforeCount, count);
  } else {
    return evolution(beforeCount, count);
  }
}

const useTrends = createMemo(getTrends);

export default function SharedWidgetsKpiInner({
  loading,
  data,
  comparaisonData,
  fragment,
  evolutionFragment,
  dataviz,
  onClick,
  className,
  disableAnimation,
  nowDate,
  isFullscreen,
}: SharedWidgetsKpiInnerProps) {
  const _hasComparaisonFragment = hasComparaisonFragment(
    fragment,
    evolutionFragment
  );
  const locale = useCurrentUserLocale();
  const metric = fragment.request.aggs.date.metrics[0];
  const range = useRangeFromFragment(fragment, loading, nowDate);
  const count = getKpiCountFromData(dataviz.dailyMetrics, data, range, metric);
  const beforeCount = getKpiCountFromData(
    dataviz.dailyMetrics,
    comparaisonData,
    range,
    metric
  );
  const displayDistributions = isBoolean(dataviz.displayDistributions)
    ? dataviz.displayDistributions
    : true;
  const trends = useTrends(data ? data.aggs.date.values : null, metric);
  return (
    <MetricTrend
      color={isColor(dataviz.color) ? dataviz.color : Color.PacificBlue}
      count={count}
      locale={locale}
      evolution={
        _hasComparaisonFragment
          ? getEvolution(metric, count, beforeCount)
          : null
      }
      data={displayDistributions ? trends : null}
      className={className}
      onClick={onClick}
      isPercentage={metric === Metric.NetSentiment}
      evolutionTooltip={tr(
        locale,
        getComparaisonPeriodFromFragments(fragment, evolutionFragment) ===
          "last_year"
          ? "KPI::TOOLTIP::EVOLUTION::PREVIOUS_YEAR"
          : "KPI::TOOLTIP::EVOLUTION::PREVIOUS_PERIOD"
      )}
      disableAnimation={disableAnimation}
      isFullscreen={isFullscreen}
    />
  );
}
